import { StateManager, ScreenRouter } from '../../managers';
import { discordAvatarUrl } from '../../discord';
import type { PresenceData, PlayerData } from '../../types';

const MAX_VISIBLE = 8;

export class PresenceBar {
  private rootEl: HTMLElement | null = null;
  private unsubscribe: (() => void) | null = null;

  initialize(container: HTMLElement): void {
    this.rootEl = document.createElement('div');
    this.rootEl.className = 'presence-bar hidden';
    container.appendChild(this.rootEl);

    this.unsubscribe = StateManager.subscribe('presenceData', () => this.render());
    this.render();
  }

  destroy(): void {
    this.unsubscribe?.();
    this.rootEl?.remove();
  }

  private render(): void {
    if (!this.rootEl) return;
    const presence = StateManager.get('presenceData') as PresenceData | undefined;
    if (!presence || presence.participants.length === 0) {
      this.rootEl.classList.add('hidden');
      return;
    }

    // Filter out self — use playerData.discordId as primary (always set from backend),
    // fall back to presence.self.id (from Discord SDK, may be null if auth failed)
    const player = StateManager.get('playerData') as PlayerData | undefined;
    const selfId = player?.discordId ?? presence.self?.id;
    const others = presence.participants.filter(p => p.id !== selfId);
    if (others.length === 0) {
      this.rootEl.classList.add('hidden');
      return;
    }

    this.rootEl.classList.remove('hidden');

    const visible = others.slice(0, MAX_VISIBLE);
    const overflow = others.length - MAX_VISIBLE;

    // Show names for 1-2 players, count for 3+
    const names = visible.map(p => p.global_name || p.nickname || p.username);
    const label = names.length <= 2
      ? names.join(' & ')
      : `${names.length} adventurers`;

    this.rootEl.innerHTML = `
      <div class="presence-avatars">
        ${visible.map(p => `
          <button class="presence-avatar" data-discord-id="${p.id}" title="${p.global_name || p.nickname || p.username}" type="button">
            <img src="${discordAvatarUrl(p.id, p.avatar, 64)}" alt="${p.username}" />
          </button>
        `).join('')}
        ${overflow > 0 ? `<span class="presence-overflow">+${overflow}</span>` : ''}
      </div>
      <span class="presence-label">${label} online</span>
    `;

    // Bind click → inspect
    this.rootEl.querySelectorAll('.presence-avatar').forEach(el => {
      el.addEventListener('click', () => {
        const discordId = (el as HTMLElement).dataset.discordId;
        if (discordId) {
          ScreenRouter.navigate('inspect', { discordId });
        }
      });
    });
  }
}
